import { createContext, FC, useReducer, useContext } from 'react';
import { createReducer } from '../../store/createReducer';
import { Filter, FiltersAction, FiltersContextValue, FiltersState } from './Filters.types';

export interface FiltersProviderProps {
  initialFilters?: Filter[];
}

const actionHandlers = {
  setFilters: (state: FiltersState, filters: Filter[]) => ({ ...state, filters })
};

const actions = ({ state, dispatch }: FiltersContextValue) => ({
  setFilters: filters => dispatch({ name: 'setFilters', payload: filters }),
  toggleFilter: (newFilter: Filter) => {
    const newFilters = [...state.filters];
    const existingFilterIndex = newFilters.findIndex(
      filter => filter.name === newFilter.name && filter.value === newFilter.value
    );

    if (existingFilterIndex > -1) newFilters.splice(existingFilterIndex, 1);
    else newFilters.push(newFilter);

    return dispatch({ name: 'setFilters', payload: newFilters });
  },
  setFilter: (filter: Filter) => {
    const newFilters = [...state.filters];
    const existingFilterIndex = newFilters.findIndex(({ name }) => name === filter.name);

    if (existingFilterIndex > -1) newFilters[existingFilterIndex] = filter;
    else newFilters.push(filter);

    return dispatch({ name: 'setFilters', payload: newFilters });
  },
  clearFilters: () => dispatch({ name: 'setFilters', payload: [] })
});

const reducer = createReducer<FiltersState, FiltersAction>({ actionHandlers });

export const FiltersContext = createContext<FiltersContextValue>(null);

export const FiltersProvider: FC<FiltersProviderProps> = ({ children, initialFilters }) => {
  const [state, dispatch] = useReducer(reducer, {}, () => ({
    filters: initialFilters || []
  }));

  return <FiltersContext.Provider value={{ state, dispatch }}>{children}</FiltersContext.Provider>;
};

export const useFilters = () => {
  const context = useContext(FiltersContext);

  if (!context) throw new Error('useFilters must be used within a FiltersProvider');

  return { ...context, actions: actions(context) };
};
